import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import axiosInstance from '../../api/axiosInstance'

const COLORS = {
    gray: '#5B6B79',
    blue: '#4680ff',
    red: '#9E2B25',
    orange: '#F29E4C',
    green: '#2CA87F',
    purple: '#7C3AED',
    teal: '#0891b2',
}

const INTEGRATIONS = [
    {
        label: 'SMS Settings',
        desc: 'Configure the SMS gateway used for patient notifications',
        icon: 'fas fa-comment-dots',
        color: COLORS.blue,
        path: '/Admin/smsSetting',
    },
    {
        label: 'Online Booking Integration',
        desc: 'Connect and sync your online appointment booking site',
        icon: 'fas fa-globe',
        color: COLORS.green,
        path: '/Admin/OnlineBookingSetting',
    },
    {
        label: 'Email Settings',
        desc: 'SMTP configuration for outgoing system emails',
        icon: 'fas fa-envelope',
        color: COLORS.orange,
        path: '/Admin/emailSetting',
    },
    {
        label: 'WhatsApp Settings',
        desc: 'Connect WhatsApp for appointment messaging',
        icon: 'ti ti-brand-whatsapp',
        color: '#25D366',
        path: '/Admin/whatsappSetting',
    },
    {
        label: 'Help Assistant',
        desc: 'Turn the how-to chatbot on or off and manage its answers',
        icon: 'ti ti-message-chatbot',
        color: COLORS.blue,
        path: '/Admin/AssistantSettings',
    },
    {
        label: 'Internal Chat',
        desc: 'Enable or disable staff-to-staff messaging',
        icon: 'ti ti-messages',
        color: COLORS.purple,
        path: '/Admin/InternalChatSettings',
    },
    {
        label: 'Assistant Agent',
        desc: 'Turn the in-chat assistant on or off and choose who can use it',
        icon: 'ti ti-robot',
        color: COLORS.teal,
        path: '/Admin/AgentSettings',
    },
]

const ApiIntegration = () => {
    const navigate = useNavigate()
    const [hovered, setHovered] = useState(null)
    const [pineHrLink, setPineHrLink] = useState('')
    const [savingPineHr, setSavingPineHr] = useState(false)

    const fetchPineHrSetting = async () => {
        try {
            const res = await axiosInstance.get('getPineHrSetting')
            if (res.data?.success) {
                setPineHrLink(res.data.data?.link || '')
            }
        } catch (error) {
            console.error('Failed to fetch PineHR setting', error)
        }
    }

    useEffect(() => { fetchPineHrSetting() }, [])

    const handlePineHrClick = async () => {
        const { value: link, isConfirmed } = await Swal.fire({
            title: 'PineHR Link',
            input: 'url',
            inputLabel: 'Enter the PineHR URL',
            inputPlaceholder: 'https://example.com',
            inputValue: pineHrLink,
            showCancelButton: true,
            confirmButtonText: 'Save',
            cancelButtonText: 'Cancel',
            inputValidator: (value) => {
                if (!value) return 'Please enter a URL'
            },
        })

        if (!isConfirmed) return

        try {
            setSavingPineHr(true)
            const res = await axiosInstance.put('updatePineHrSetting', { link })
            if (res.data?.success) {
                setPineHrLink(link)
                Swal.fire('Saved', 'PineHR link updated successfully', 'success')
            } else {
                Swal.fire('Error', 'Failed to save PineHR link', 'error')
            }
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Something went wrong', 'error')
        } finally {
            setSavingPineHr(false)
        }
    }

    const cards = [
        ...INTEGRATIONS,
        {
            label: 'PineHR',
            desc: pineHrLink ? pineHrLink : 'Set and save the PineHR link',
            icon: 'fas fa-link',
            color: COLORS.purple,
            onClick: handlePineHrClick,
        },
    ]

    return (
        <div className='pc-container'>
            <div className='pc-content'>

                {/* ── Page Header ── */}
                <div style={{
                    background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0',
                    padding: '16px 24px', marginBottom: 20,
                    display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    <i
                        className="fa fa-arrow-left"
                        onClick={() => navigate(-1)}
                        style={{ cursor: 'pointer', color: COLORS.gray, fontSize: 16 }}
                    />
                    <div style={{
                        width: 38, height: 38, borderRadius: 10,
                        background: `${COLORS.teal}15`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <i className="ti ti-plug" style={{ color: COLORS.teal, fontSize: 20 }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#1a202c' }}>API Integration</div>
                        <div style={{ fontSize: 12, color: COLORS.gray }}>Manage third-party connections and integrations</div>
                    </div>
                </div>

                {/* ── Integrations Grid ── */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: 16,
                }}>
                    {cards.map((s, i) => {
                        const isHov = hovered === i
                        const isPineHr = s.label === 'PineHR'
                        return (
                            <button
                                key={s.label}
                                onClick={() => (isPineHr ? s.onClick() : navigate(s.path))}
                                onMouseEnter={() => setHovered(i)}
                                onMouseLeave={() => setHovered(null)}
                                disabled={isPineHr && savingPineHr}
                                style={{
                                    background: '#fff',
                                    border: `1.5px solid ${isHov ? s.color : '#e8ecf0'}`,
                                    borderRadius: 12,
                                    padding: '20px 20px',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    transition: 'border-color 0.18s, box-shadow 0.18s, transform 0.12s',
                                    boxShadow: isHov ? `0 4px 18px ${s.color}22` : '0 1px 4px rgba(0,0,0,0.04)',
                                    transform: isHov ? 'translateY(-2px)' : 'none',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                {/* Icon */}
                                <div style={{
                                    width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                                    background: isHov ? s.color : `${s.color}15`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transition: 'background 0.18s',
                                }}>
                                    <i className={s.icon} style={{
                                        fontSize: 22,
                                        color: isHov ? '#fff' : s.color,
                                        transition: 'color 0.18s',
                                    }} />
                                </div>

                                {/* Text */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        fontSize: 14, fontWeight: 700,
                                        color: isHov ? s.color : '#1a202c',
                                        marginBottom: 4, transition: 'color 0.18s',
                                    }}>
                                        {s.label}
                                    </div>
                                    <div style={{
                                        fontSize: 12, color: COLORS.gray, lineHeight: 1.4,
                                        overflow: 'hidden', textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {s.desc}
                                    </div>
                                </div>

                                {/* Arrow */}
                                <i className={isPineHr ? 'ti ti-edit' : 'ti ti-chevron-right'} style={{
                                    fontSize: 16, color: isHov ? s.color : '#ccc',
                                    alignSelf: 'center', flexShrink: 0, transition: 'color 0.18s',
                                }} />
                            </button>
                        )
                    })}
                </div>

            </div>
        </div>
    )
}

export default ApiIntegration
